import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Avatar,
  Button,
  Grid,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Badge
} from '@mui/material';
import {
  Edit,
  School,
  EmojiEvents,
  TrendingUp,
  Settings,
  Person,
  Email,
  Notifications,
  Security,
  Public,
  CheckCircle,
  AccessTime,
  Grade
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useProgressTracking } from '../../hooks/useProgressTracking';

const UserProfile = ({ open, onClose }) => {
  const { user, updateProfile, logout } = useAuth();
  const { getOverallStats, completedCourses, enrolledCourses } = useProgressTracking();
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    notifications: user?.preferences?.notifications || true,
    publicProfile: user?.preferences?.publicProfile || false
  });
  const [loading, setLoading] = useState(false);

  const stats = getOverallStats();

  const handleSave = async () => {
    setLoading(true);
    try {
      await updateProfile({
        name: formData.name,
        email: formData.email,
        preferences: {
          ...user.preferences,
          notifications: formData.notifications,
          publicProfile: formData.publicProfile
        }
      });
      setEditMode(false);
    } catch (error) {
      console.error('Failed to update profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      name: user?.name || '',
      email: user?.email || '',
      notifications: user?.preferences?.notifications || true,
      publicProfile: user?.preferences?.publicProfile || false
    });
    setEditMode(false);
  };

  const getProgressLevel = (completedCount) => {
    if (completedCount >= 10) return { level: 'Expert', color: '#9c27b0', icon: '🎓' };
    if (completedCount >= 5) return { level: 'Advanced', color: '#f44336', icon: '🔥' };
    if (completedCount >= 2) return { level: 'Intermediate', color: '#ff9800', icon: '📚' };
    return { level: 'Beginner', color: '#4caf50', icon: '🌱' };
  };

  const progressLevel = getProgressLevel(completedCourses.length);

  if (!user) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h5">User Profile</Typography>
          <Button onClick={onClose}>Close</Button>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Grid container spacing={3}>
          {/* Profile Overview */}
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3, textAlign: 'center' }}>
              <Badge
                badgeContent={progressLevel.icon}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              >
                <Avatar
                  sx={{
                    width: 80,
                    height: 80,
                    bgcolor: progressLevel.color,
                    fontSize: '2rem',
                    mx: 'auto',
                    mb: 2
                  }}
                >
                  {user.name.charAt(0).toUpperCase()}
                </Avatar>
              </Badge>

              {editMode ? (
                <Box sx={{ mb: 2 }}>
                  <TextField
                    fullWidth
                    label="Name"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    margin="normal"
                    size="small"
                  />
                  <TextField
                    fullWidth
                    label="Email"
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    margin="normal"
                    size="small"
                    type="email"
                  />
                </Box>
              ) : (
                <>
                  <Typography variant="h6" gutterBottom>
                    {user.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {user.email}
                  </Typography>
                </>
              )}

              <Chip
                label={`${progressLevel.level} Learner`}
                sx={{
                  backgroundColor: progressLevel.color + '20',
                  color: progressLevel.color,
                  fontWeight: 'bold'
                }}
              />

              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Member since {new Date(user.joinDate).toLocaleDateString()}
              </Typography>
            </Paper>
          </Grid>

          {/* Stats and Progress */}
          <Grid item xs={12} md={8}>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={6} md={3}>
                <Card>
                  <CardContent sx={{ textAlign: 'center', py: 2 }}>
                    <School sx={{ color: 'primary.main', mb: 1 }} />
                    <Typography variant="h5">{stats.totalCompleted}</Typography>
                    <Typography variant="caption">Completed</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} md={3}>
                <Card>
                  <CardContent sx={{ textAlign: 'center', py: 2 }}>
                    <AccessTime sx={{ color: 'warning.main', mb: 1 }} />
                    <Typography variant="h5">{stats.activeCourses}</Typography>
                    <Typography variant="caption">In Progress</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} md={3}>
                <Card>
                  <CardContent sx={{ textAlign: 'center', py: 2 }}>
                    <TrendingUp sx={{ color: 'success.main', mb: 1 }} />
                    <Typography variant="h5">{stats.averageProgress}%</Typography>
                    <Typography variant="caption">Avg Progress</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} md={3}>
                <Card>
                  <CardContent sx={{ textAlign: 'center', py: 2 }}>
                    <EmojiEvents sx={{ color: 'error.main', mb: 1 }} />
                    <Typography variant="h5">{user.achievements?.length || 0}</Typography>
                    <Typography variant="caption">Achievements</Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Learning Progress */}
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Learning Progress
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">Overall Completion</Typography>
                  <Typography variant="body2">{stats.averageProgress}%</Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={stats.averageProgress}
                  sx={{ height: 8, borderRadius: 4 }}
                />
              </Box>
              
              <Typography variant="body2" color="text.secondary">
                You've completed {stats.totalCompleted} out of {stats.totalCourses} enrolled courses
              </Typography>
            </Paper>

            {/* Settings */}
            <Paper sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">Settings</Typography>
                {!editMode ? (
                  <Button
                    startIcon={<Edit />}
                    onClick={() => setEditMode(true)}
                  >
                    Edit
                  </Button>
                ) : (
                  <Box>
                    <Button onClick={handleCancel} sx={{ mr: 1 }}>
                      Cancel
                    </Button>
                    <Button
                      variant="contained"
                      onClick={handleSave}
                      disabled={loading}
                    >
                      Save
                    </Button>
                  </Box>
                )}
              </Box>

              <List>
                <ListItem>
                  <ListItemIcon>
                    <Notifications />
                  </ListItemIcon>
                  <ListItemText
                    primary="Email Notifications"
                    secondary="Receive updates about your courses"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.notifications}
                        onChange={(e) => setFormData(prev => ({ ...prev, notifications: e.target.checked }))}
                        disabled={!editMode}
                      />
                    }
                    label=""
                  />
                </ListItem>
                
                <ListItem>
                  <ListItemIcon>
                    <Public />
                  </ListItemIcon>
                  <ListItemText
                    primary="Public Profile"
                    secondary="Allow others to see your learning progress"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.publicProfile}
                        onChange={(e) => setFormData(prev => ({ ...prev, publicProfile: e.target.checked }))}
                        disabled={!editMode}
                      />
                    }
                    label=""
                  />
                </ListItem>
              </List>
            </Paper>
          </Grid>

          {/* Achievements */}
          {user.achievements && user.achievements.length > 0 && (
            <Grid item xs={12}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Achievements
                </Typography>
                <Grid container spacing={2}>
                  {user.achievements.map((achievement, index) => (
                    <Grid item xs={12} sm={6} md={4} key={index}>
                      <Card variant="outlined">
                        <CardContent>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <EmojiEvents sx={{ color: 'warning.main', mr: 1 }} />
                            <Typography variant="subtitle1">
                              {achievement.title}
                            </Typography>
                          </Box>
                          <Typography variant="body2" color="text.secondary">
                            {achievement.description}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Paper>
            </Grid>
          )}
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={logout} color="error">
          Logout
        </Button>
        <Button onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UserProfile;